import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardTitle, Notice, EmptyState, Pill } from '../ui';
import {
    DeviceHero, PostureBadge, HealthStrip, HealthTile, DeviceLayout, DeviceNav,
    NavItem, SpecGrid, SpecFields, StorageBar, MachineTag, SourceTags,
    LienTexte, MonoCell, Muted,
} from './security.styled';
import { formatDate } from './severity';
import ComplianceDetail from './complianceDetail';
import DeviceVulnerabilities from './deviceVulnerabilities';
import DeviceApplications from './deviceApplications';
import { Champ } from './champ';

/* ═══════════════════════════════════════════════════════════════════
   Fiche appareil : une section à la fois.

   Structure reprise du projet SI-Securisation — bandeau d'identité, tuiles
   d'état cliquables, navigation latérale groupée par thème. Notre logique et
   notre palette sont inchangées : mêmes données, mêmes composants de détail
   (conformité, vulnérabilités, applications), mêmes couleurs sémantiques.

   Ce que le découpage par THÈME change, et pourquoi il vaut mieux que
   l'empilement précédent : la fiche portait sept blocs à la suite, dont trois
   qui chargent à la demande. Il fallait défiler pour savoir ce qu'elle
   contenait. Les sections nomment le contenu d'avance, et les tuiles y mènent.
   Le découpage par SOURCE (« ce que dit Intune », « ce que dit Defender ») a
   été écarté : personne ne cherche une source, on cherche un numéro de série.
   ═══════════════════════════════════════════════════════════════════ */

const RISK_TONE = { High: 'danger', Medium: 'warn', Low: 'accent', None: 'ok', Informational: 'ok' };
const EXPOSURE_TONE = { High: 'danger', Medium: 'warn', Low: 'ok', None: 'ok' };
const VALUE_LABEL = { High: 'Élevée', Normal: 'Normale', Low: 'Faible' };

const PANES = [
    { key: 'overview', label: "Vue d'ensemble", icon: 'bx-layout' },
    {
        group: 'Matériel',
        items: [
            { key: 'os', label: "Système d'exploitation", icon: 'bx-window-alt' },
            { key: 'hardware', label: 'Matériel', icon: 'bx-chip' },
            { key: 'identity', label: 'Identité & réseau', icon: 'bx-id-card' },
            { key: 'applications', label: 'Applications', icon: 'bx-package' },
        ],
    },
    {
        group: 'Sécurité',
        items: [
            { key: 'compliance', label: 'Conformité', icon: 'bx-shield-quarter' },
            { key: 'vulnerabilities', label: 'Vulnérabilités', icon: 'bx-target-lock' },
        ],
    },
];

// Octets → Go. Les champs de managedDevice sont en octets et ne sont présentés
// autrement nulle part dans l'application.
function formatOctets(octets) {
    const n = Number(octets);
    if (!octets || Number.isNaN(n) || n <= 0) return null;
    const go = n / 1024 ** 3;
    return `${go < 0.1 ? '< 0,1' : go.toFixed(1).replace('.', ',')} Go`;
}

// Au-delà de ce nombre de CVE à exploit public, le poste passe en « à traiter »
// et non plus en « à surveiller » : 12 appareils sur 1 036 (mesuré le
// 2026-09-03), ce qui en fait une vraie exception et non un état courant.
const SEUIL_EXPLOITS_CRITIQUE = 10;

/**
 * Verdict de posture, calculé depuis nos propres compteurs.
 *
 * Chaque règle produit sa RAISON : un verdict sans motif se conteste, un
 * verdict motivé se traite.
 *
 * LES SEUILS SONT CALIBRÉS SUR LE PARC, pas devinés. Première version écartée
 * après mesure du 2026-09-03 sur les 1 036 appareils Intune actifs :
 *   • « au moins une CVE critique »  → 811 appareils, médiane 12 CVE
 *   • « exposition Defender Medium » → 818 appareils
 * Deux signaux qui ne séparent rien : ils faisaient passer 86 % du parc en
 * alerte, et un verdict vrai partout ne se lit plus. Remplacés par des signaux
 * effectivement rares :
 *   • paramètre de conformité en échec →  44 appareils
 *   • CVE à EXPLOIT PUBLIC             → 277 appareils
 *   • exposition Defender « High »     →  67 appareils
 * Répartition obtenue : 10 % à traiter, 46 % à surveiller, 44 % sain.
 *
 * L'échec de déploiement d'une application requise concerne 403 appareils : réel
 * et actionnable, mais trop répandu pour teindre la fiche en rouge — il pèse
 * donc « à surveiller » et non « à traiter ».
 */
function calculerPosture(data) {
    const raisons = [];
    let tone = 'ok';
    const degrader = (niveau, raison) => {
        raisons.push(raison);
        if (niveau === 'danger' || tone === 'ok') tone = niveau;
    };

    const failing = data.compliance?.failingSettings || 0;
    if (failing > 0) {
        degrader('danger', `${failing} paramètre${failing > 1 ? 's' : ''} de conformité en échec`);
    }

    if (data.defender?.exposureLevel === 'High') {
        degrader('danger', 'exposition Defender élevée');
    }

    // Les CVE à exploit public, et non les CVE critiques : 312 CVE du catalogue
    // sur 13 830 ont un exploit connu, c'est ce qui distingue un risque
    // théorique d'un risque exploitable aujourd'hui.
    const exploits = data.vulnerabilities?.available ? (data.vulnerabilities.withExploit || 0) : 0;
    if (exploits >= SEUIL_EXPLOITS_CRITIQUE) {
        degrader('danger', `${exploits} CVE à exploit public`);
    } else if (exploits > 0) {
        degrader('warn', `${exploits} CVE à exploit public`);
    }

    const appsEnEchec = data.applications?.requisesEnEchec || 0;
    if (appsEnEchec > 0) {
        degrader('warn', `${appsEnEchec} application${appsEnEchec > 1 ? 's' : ''} requise${appsEnEchec > 1 ? 's' : ''} en échec`);
    }

    // L'exclusion Defender ne dégrade pas la posture réelle du poste — elle rend
    // la mesure INCERTAINE, ce qui n'est pas la même chose et doit se lire
    // autrement : les compteurs affichés ne sont plus rafraîchis.
    if (data.defender?.isExcluded) {
        degrader('warn', 'fiche Defender exclue du suivi');
    }

    const libelle = tone === 'danger' ? 'À traiter' : tone === 'warn' ? 'À surveiller' : 'Sain';
    return { tone, libelle, raisons };
}

function SourceBadges({ sources }) {
    return (
        <SourceTags>
            <span className={sources.entra ? 'on' : undefined} title="Entra ID">E</span>
            <span className={sources.intune ? 'on' : undefined} title="Intune">I</span>
            <span className={sources.defender ? 'on' : undefined} title="Defender">D</span>
        </SourceTags>
    );
}

/* ─── Sections de caractéristiques ─── */

function CarteOs({ data }) {
    const { identity, defender, entra } = data;
    return (
        <Card $pad="16px">
            <CardTitle>Système d'exploitation</CardTitle>
            <SpecFields>
                <Champ label="Famille">{identity.os}</Champ>
                <Champ label="Version" className="mono">{identity.osVersion}</Champ>
                <Champ label="Build" className="mono">{defender?.osBuild}</Champ>
                <Champ label="Architecture">{defender?.osArchitecture}</Champ>
                <Champ label="Type d'enrôlement">{entra?.enrollmentType}</Champ>
                <Champ label="Version de l'agent" className="mono">{defender?.agentVersion}</Champ>
            </SpecFields>
        </Card>
    );
}

function CarteMateriel({ data }) {
    const { identity, entra } = data;
    const total = Number(entra?.totalStorageSpaceInBytes) || 0;
    const libre = Number(entra?.freeStorageSpaceInBytes) || 0;
    const pourcent = total > 0 ? Math.min(100, Math.max(0, Math.round(((total - libre) / total) * 100))) : null;

    return (
        <Card $pad="16px">
            <CardTitle>Matériel</CardTitle>
            <SpecFields>
                <Champ label="Modèle">
                    {[identity.manufacturer, identity.model].filter(Boolean).join(' ') || null}
                </Champ>
                <Champ label="N° de série" className="mono">{identity.serialNumber}</Champ>
                <Champ label="Mémoire">{formatOctets(entra?.physicalMemoryInBytes)}</Champ>
                <Champ label="Propriété">{entra?.deviceOwnership}</Champ>
                {/* Chiffrement et root sont les deux seuls champs de cette carte
                    qui portent un jugement : ils sont donc colorés, le reste non. */}
                <Champ
                    label="Chiffrement"
                    className={entra?.isEncrypted === true ? 'ok' : entra?.isEncrypted === false ? 'danger' : ''}
                >
                    {entra?.isEncrypted === true ? 'Oui' : entra?.isEncrypted === false ? 'Non' : null}
                </Champ>
                <Champ label="Root / jailbreak" className={entra?.isRooted ? 'danger' : 'ok'}>
                    {entra?.isRooted === true ? 'Oui' : entra?.isRooted === false ? 'Non' : null}
                </Champ>
                <Champ label="Inscrit le" className="mono">
                    {entra?.createdDateTime ? formatDate(entra.createdDateTime) : null}
                </Champ>
                <Champ label="MAC Ethernet" className="mono">{entra?.ethernetMacAddress}</Champ>
                {pourcent !== null && (
                    <div className="full">
                        <dt>Stockage</dt>
                        <StorageBar $percent={pourcent}>
                            <div className="track"><div className="fill" /></div>
                            <div className="legend">
                                <span>{formatOctets(total - libre)} utilisés ({pourcent} %)</span>
                                <span>{formatOctets(libre)} libres / {formatOctets(total)}</span>
                            </div>
                        </StorageBar>
                    </div>
                )}
            </SpecFields>
        </Card>
    );
}

// Adresse IP réellement active : on préfère une interface dont Defender dit
// qu'elle est « Up », en écartant le lien-local 169.254 que portent les
// interfaces désactivées. Repli sur la dernière IP connue, puis sur l'IP
// externe — seule renseignée sur certains mobiles.
function ipActive(defender) {
    const liste = Array.isArray(defender?.ipAddresses) ? defender.ipAddresses : [];
    const active = liste.find((ip) => ip.operationalStatus === 'Up'
        && ip.ipAddress && !String(ip.ipAddress).startsWith('169.254.'));
    return active?.ipAddress || defender?.lastIpAddress || defender?.lastExternalIpAddress || null;
}

function CarteIdentite({ data }) {
    const { identity, defender, entra, compliance } = data;
    const tags = Array.isArray(defender?.machineTags) ? defender.machineTags : [];

    return (
        <Card $pad="16px">
            <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                Identité & réseau
                <SourceBadges sources={data.sources} />
            </CardTitle>
            <SpecFields>
                <Champ label="Dernière connexion Entra">
                    {entra?.approximateLastSignInDateTime ? formatDate(entra.approximateLastSignInDateTime) : null}
                </Champ>
                <Champ label="Vu par Defender">
                    {defender?.lastSeen ? formatDate(defender.lastSeen) : null}
                </Champ>
                <Champ label="Détail conformité collecté le">
                    {compliance?.lastSync ? formatDate(compliance.lastSync) : null}
                </Champ>
                <Champ label="Adresse IP" className="mono">{ipActive(defender)}</Champ>
                <Champ label="Groupe Defender">{defender?.rbacGroupName}</Champ>
                <Champ label="Gestion">{entra?.managementType || defender?.managedBy}</Champ>
                <Champ label="État du capteur">{defender?.healthStatus}</Champ>
                <Champ label="Onboarding">{defender?.onboardingStatus}</Champ>
                <Champ label="Device Id Azure" className="mono">{identity.aadDeviceId}</Champ>
                <Champ label="Device Id Intune" className="mono">{identity.intuneDeviceId}</Champ>
                <Champ label="Identifiant Defender" className="mono">{identity.defenderDeviceId}</Champ>
                <Champ label="MAC Wi-Fi" className="mono">{entra?.wiFiMacAddress}</Champ>
                {tags.length > 0 && (
                    <div className="full">
                        <dt>Étiquettes Defender</dt>
                        <div style={{ marginTop: '4px' }}>
                            {tags.map((t) => <MachineTag key={t}>{t}</MachineTag>)}
                        </div>
                    </div>
                )}
            </SpecFields>
        </Card>
    );
}

/* La fiche utilisateur se résout par UPN — c'est la clé qu'attend la recherche
   de la page Utilisateurs, et le paramètre `user` la lui passe directement. */
function LienUtilisateur({ upn, children }) {
    return (
        <LienTexte
            as={Link}
            to={`/Users?user=${encodeURIComponent(upn)}`}
            title={`Ouvrir la fiche de ${upn}`}
        >
            {children}
        </LienTexte>
    );
}

function CarteProprietaire({ owner }) {
    if (!owner) return null;
    const absent = owner.isDeleted === true;
    return (
        <Card $pad="16px">
            <CardTitle>Propriétaire</CardTitle>
            <SpecFields>
                <Champ label="Nom">
                    {owner.userPrincipalName
                        ? <LienUtilisateur upn={owner.userPrincipalName}>{owner.displayName}</LienUtilisateur>
                        : owner.displayName}
                </Champ>
                {/* L'UPN EST la clé de la fiche utilisateur : c'est lui qu'on
                    clique pour passer de l'appareil à son porteur. */}
                <Champ label="UserPrincipalName" full>
                    {owner.userPrincipalName
                        ? (
                            <LienUtilisateur upn={owner.userPrincipalName}>
                                <MonoCell>{owner.userPrincipalName}</MonoCell>
                            </LienUtilisateur>
                        )
                        : null}
                </Champ>
                <Champ label="Mail" className="mono">{owner.mail}</Champ>
                <Champ label="Étoile">{owner.department}</Champ>
                <Champ label="Poste">{owner.jobTitle}</Champ>
                {/* Un porteur disparu de l'annuaire se dit AVANT le clic :
                    sinon on arrive sur une fiche archivée sans l'avoir demandé. */}
                <Champ label="Compte" className={absent ? undefined : owner.accountEnabled ? 'ok' : 'danger'}>
                    {absent
                        ? (
                            <>
                                <Pill $tone="warn">Hors annuaire</Pill>
                                {' '}
                                <Muted>
                                    dernier état connu&nbsp;: {owner.accountEnabled ? 'actif' : 'désactivé'}
                                </Muted>
                            </>
                        )
                        : owner.accountEnabled ? 'Actif' : 'Désactivé'}
                </Champ>
            </SpecFields>
        </Card>
    );
}

/* ─── Fiche ─── */

export default function DeviceOverviewView({ data }) {
    const [pane, setPane] = useState('overview');

    const notInDefender = !data.sources.defender;
    const posture = calculerPosture(data);
    const vulns = data.vulnerabilities;
    const compliance = data.compliance;
    const apps = data.applications || {};

    // Les tuiles nomment leur section de destination. Le ton suit ce qui est
    // actionnable, pas les agrégats.
    const tuiles = [
        {
            pane: 'compliance',
            icon: 'bx-shield-quarter',
            tone: compliance.failingSettings > 0 ? 'danger' : compliance.nonCompliant > 0 ? 'warn' : 'ok',
            value: compliance.managedByIntune ? `${compliance.compliant}/${compliance.total}` : '—',
            label: compliance.failingSettings > 0
                ? `${compliance.failingSettings} paramètre${compliance.failingSettings > 1 ? 's' : ''} en échec`
                : 'Conformité Intune',
        },
        {
            pane: 'vulnerabilities',
            icon: 'bx-target-lock',
            tone: vulns.available && vulns.critical > 0 ? 'danger' : vulns.available && vulns.total > 0 ? 'warn' : 'ok',
            value: vulns.available ? vulns.total.toLocaleString('fr-FR') : '—',
            label: vulns.available ? `${vulns.critical} critiques, ${vulns.high} hautes` : 'Hors Defender',
        },
        {
            pane: 'applications',
            icon: 'bx-package',
            tone: apps.requisesEnEchec > 0 ? 'danger' : 'ok',
            value: apps.managedByIntune ? (apps.detectees ?? 0).toLocaleString('fr-FR') : '—',
            label: apps.requisesEnEchec > 0
                ? `${apps.requisesEnEchec} requise${apps.requisesEnEchec > 1 ? 's' : ''} en échec`
                : 'Applications découvertes',
        },
        {
            pane: 'identity',
            icon: 'bx-tachometer',
            tone: EXPOSURE_TONE[data.defender?.exposureLevel] || 'neutral',
            value: data.defender?.exposureLevel || '—',
            label: 'Exposition Defender',
        },
        {
            pane: 'identity',
            icon: 'bx-diamond',
            tone: RISK_TONE[data.defender?.riskScore] || 'neutral',
            value: VALUE_LABEL[data.defender?.deviceValue] || data.defender?.deviceValue || '—',
            label: `Criticité · risque ${data.defender?.riskScore || 'inconnu'}`,
        },
    ];

    return (
        <>
            <DeviceHero>
                <i className="bx bx-laptop" />
                <div>
                    <div className="eyebrow">Fiche appareil · Entra + Intune + Defender</div>
                    <h2 className="name">{data.identity.entraName || data.identity.defenderName}</h2>
                    <div className="facts">
                        {data.owner && (
                            <span>
                                Utilisateur :{' '}
                                {data.owner.userPrincipalName
                                    ? (
                                        <LienUtilisateur upn={data.owner.userPrincipalName}>
                                            <b>{data.owner.displayName}</b>
                                        </LienUtilisateur>
                                    )
                                    : <b>{data.owner.displayName}</b>}
                            </span>
                        )}
                        {data.identity.os && <span>{data.identity.os} <b>{data.identity.osVersion}</b></span>}
                        {data.defender?.lastSeen && <span>Defender : vu le <b>{formatDate(data.defender.lastSeen)}</b></span>}
                    </div>
                </div>
                <PostureBadge $tone={posture.tone}>
                    <div className="verdict">
                        <i className={`bx ${posture.tone === 'ok' ? 'bx-check-shield' : 'bx-error'}`} />
                        {posture.libelle}
                    </div>
                    {posture.raisons.length > 0 && (
                        <div className="reasons">{posture.raisons.join(' · ')}</div>
                    )}
                </PostureBadge>
            </DeviceHero>

            {/* Les avertissements de contexte restent HORS des sections : ils
                changent la lecture de toute la fiche, pas d'un onglet. */}
            {data.identity.namesDiffer && (
                <Notice $tone="accent" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-info-circle" />
                    <span>
                        Nommé <strong>{data.identity.entraName}</strong> dans Entra et{' '}
                        <strong>{data.identity.defenderName}</strong> dans Defender. Le rapprochement
                        se fait sur l'identifiant Entra, pas sur le nom.
                    </span>
                </Notice>
            )}

            {data.defender?.isExcluded && (
                <Notice $tone="warn" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-hide" />
                    <span>
                        La fiche Defender de cet appareil est <strong>exclue du suivi</strong>
                        {data.defender.exclusionReason && <> — motif <strong>{data.defender.exclusionReason}</strong></>}.
                        Les vulnérabilités et l'état du capteur datent de la dernière collecte
                        avant exclusion et ne sont plus rafraîchis.
                    </span>
                </Notice>
            )}

            {notInDefender && (
                <Notice $tone="warn" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error" />
                    Cet appareil n'est pas onboardé dans Defender : aucune vulnérabilité ni posture
                    de risque n'est disponible. 849 appareils Entra actifs sont dans ce cas.
                </Notice>
            )}

            <HealthStrip>
                {tuiles.map((t, i) => (
                    <HealthTile
                        key={i}
                        type="button"
                        $tone={t.tone}
                        $active={pane === t.pane}
                        onClick={() => setPane(t.pane)}
                        title={`Voir la section ${t.pane}`}
                    >
                        <i className={`bx ${t.icon}`} />
                        <span>
                            <span className="value">{t.value}</span>
                            <br />
                            <span className="label">{t.label}</span>
                        </span>
                    </HealthTile>
                ))}
            </HealthStrip>

            <DeviceLayout>
                <DeviceNav>
                    {PANES.map((section) => (section.group ? (
                        <div key={section.group}>
                            <div className="group">{section.group}</div>
                            {section.items.map((item) => (
                                <NavItem
                                    key={item.key}
                                    type="button"
                                    $active={pane === item.key}
                                    onClick={() => setPane(item.key)}
                                >
                                    <i className={`bx ${item.icon}`} />
                                    {item.label}
                                    {item.key === 'compliance' && compliance.failingSettings > 0 && (
                                        <span className="badge">{compliance.failingSettings}</span>
                                    )}
                                    {item.key === 'vulnerabilities' && vulns.available && vulns.critical > 0 && (
                                        <span className="badge">{vulns.critical}</span>
                                    )}
                                    {item.key === 'applications' && apps.requisesEnEchec > 0 && (
                                        <span className="badge">{apps.requisesEnEchec}</span>
                                    )}
                                </NavItem>
                            ))}
                        </div>
                    ) : (
                        <NavItem
                            key={section.key}
                            type="button"
                            $active={pane === section.key}
                            onClick={() => setPane(section.key)}
                        >
                            <i className={`bx ${section.icon}`} />
                            {section.label}
                        </NavItem>
                    )))}
                </DeviceNav>

                <div>
                    {pane === 'overview' && (
                        <SpecGrid>
                            <CarteOs data={data} />
                            <CarteMateriel data={data} />
                            <CarteIdentite data={data} />
                            <CarteProprietaire owner={data.owner} />
                        </SpecGrid>
                    )}

                    {pane === 'os' && <SpecGrid><CarteOs data={data} /></SpecGrid>}
                    {pane === 'hardware' && <SpecGrid><CarteMateriel data={data} /></SpecGrid>}
                    {pane === 'identity' && (
                        <SpecGrid>
                            <CarteIdentite data={data} />
                            <CarteProprietaire owner={data.owner} />
                        </SpecGrid>
                    )}

                    {/* Les trois sections lourdes gardent leurs composants
                        existants, inchangés : ils portent déjà leur propre
                        chargement à la demande, leurs filtres et leurs exports. */}
                    {pane === 'applications' && (
                        apps.managedByIntune ? (
                            <DeviceApplications
                                applications={apps}
                                deviceIdIntune={data.identity.intuneDeviceId}
                                deviceName={data.identity.entraName || data.identity.defenderName}
                                defaultExpanded
                            />
                        ) : (
                            <Card>
                                <EmptyState>
                                    <i className="bx bx-package" />
                                    <h3>Non géré par Intune</h3>
                                    <p>Sans gestion Intune, ni déploiement ni inventaire logiciel n'est collecté.</p>
                                </EmptyState>
                            </Card>
                        )
                    )}

                    {pane === 'compliance' && (
                        <ComplianceDetail
                            compliance={compliance}
                            deviceName={data.identity.entraName || data.identity.defenderName}
                            deviceKey={data.identity.intuneDeviceId || data.identity.aadDeviceId || data.identity.entraName}
                        />
                    )}

                    {pane === 'vulnerabilities' && (
                        vulns.available && vulns.total > 0 ? (
                            <DeviceVulnerabilities
                                vulnerabilities={vulns}
                                deviceId={data.identity.defenderDeviceId}
                                hostname={data.identity.defenderName || data.identity.entraName}
                            />
                        ) : (
                            <Card>
                                <EmptyState>
                                    <i className="bx bx-check-shield" />
                                    <h3>{vulns.available ? 'Aucune vulnérabilité' : 'Hors Defender'}</h3>
                                    <p>
                                        {vulns.available
                                            ? 'Aucune CVE détectée sur cet appareil.'
                                            : "Cet appareil n'est pas onboardé dans Defender."}
                                    </p>
                                </EmptyState>
                            </Card>
                        )
                    )}
                </div>
            </DeviceLayout>
        </>
    );
}
